import type { Uuid } from "@/domains/global/identifiers";
import type { IAudioUploadingStore } from "@/store/audio/uploading/audio-uploading.types";

import { TerminalOutcome } from "./audio-pipeline.types";
import { failResult, okResult, type StageResult } from "./stage-result";

/**
 * Upload-stage adapter. Enqueues the upload, waits for it to reach a terminal
 * state, and maps the result into the pipeline's typed outcome space. This is
 * used for the retranscribe-path upload where the pipeline must block on the
 * source landing in cloud storage before backend transcribe-from-source runs.
 *
 * Happy-path background uploads (local transcript available) do not go
 * through this adapter — they are started directly as a fire-and-forget
 * worker.
 */
export async function runUploadStage({
  audioRecordingId,
  uploadingStore,
  forceRestart = false,
}: {
  audioRecordingId: Uuid;
  uploadingStore: IAudioUploadingStore;
  forceRestart?: boolean;
}): Promise<StageResult> {
  const existingTerminalResult = forceRestart
    ? null
    : uploadingStore.getTerminalUploadResult({ audioRecordingId });

  if (existingTerminalResult?.success) {
    return okResult();
  }

  await uploadingStore.enqueue({ audioRecordingId, forceRestart });

  const result = await uploadingStore.waitForUploadComplete({ audioRecordingId });
  if (result.success) {
    return okResult();
  }

  return failResult(TerminalOutcome.UploadFailed, {
    kind: "upload-failed",
    message: result.error || "Upload failed",
  });
}
